// SPDX-License-Identifier: MIT
/**
 * Interface motion, in the scene's beats: a cursor that travels and clicks on the beat, typing with
 * a human rhythm, boxes morphing between states, digits rolling into a fact, and a card tilt.
 * Everything is a pure function of the beat (and a seed), like the rest of the toolkit.
 */
import { cubicBezier } from '../../core/bezier.ts';
import { clamp, fract, lerp } from '../../core/math.ts';
import { hash01 } from '../../core/rng.ts';
import type { SceneEnv, SceneSoundEvent } from '../../scene.ts';
import { chars } from '../../type.ts';
import { mix } from '../color.ts';
import type { Box } from './draw.ts';
type Ctx = CanvasRenderingContext2D;
type Beat = Pick<SceneEnv, 'localBeat'>;
// --- Cursor -------------------------------------------------------------------------------------------
/** Where the cursor is at a local beat; `click` presses there exactly on that beat. */
export type Waypoint = {
    beat: number;
    x: number;
    y: number;
    click?: boolean;
};
export type CursorState = {
    x: number;
    y: number;
    /** 0–1 button press. */
    press: number;
    /** Beats since the latest click (Infinity before the first one), and where it was. */
    sinceClick: number;
    clickAt: [
        number,
        number
    ];
};
/** Quick to leave, long to land: how a hand moves a pointer onto a target. */
const reach = cubicBezier(0.45, 0, 0.15, 1);
/**
 * Cursor travel through `points` (sorted by beat). The cursor arrives `lead` beats before each
 * waypoint, so a click lands on its beat, and takes at most `travel` beats per move, on a slight
 * arc. Before the first waypoint it rests there; after the last it stays.
 */
export function cursorAt(env: Beat, points: Waypoint[], { lead = 0.16, travel = 0.85 }: {
    lead?: number;
    travel?: number;
} = {}): CursorState {
    const b = env.localBeat;
    let x = points[0]?.x ?? 0;
    let y = points[0]?.y ?? 0;
    for (let i = 1; i < points.length; i++) {
        const p0 = points[i - 1]!;
        const p1 = points[i]!;
        const arrive = p1.beat - lead;
        // After a click the hand stays put for a moment before it moves on.
        const leave = Math.max(p0.beat + (p0.click ? 0.2 : 0), arrive - travel);
        if (b < leave)
            break;
        const k = reach(clamp((b - leave) / Math.max(1e-6, arrive - leave)));
        // Arc: bow the path sideways by 10% of its length, always towards the top of the frame.
        const dx = p1.x - p0.x;
        const dy = p1.y - p0.y;
        const len = Math.hypot(dx, dy);
        let nx = -dy / (len || 1);
        let ny = dx / (len || 1);
        if (ny > 0)
            [nx, ny] = [-nx, -ny];
        const bow = 4 * k * (1 - k) * len * 0.1;
        x = lerp(p0.x, p1.x, k) + nx * bow;
        y = lerp(p0.y, p1.y, k) + ny * bow;
    }
    let press = 0;
    let sinceClick = Infinity;
    let clickAt: [
        number,
        number
    ] = [x, y];
    for (const p of points) {
        if (!p.click)
            continue;
        const d = b - p.beat;
        // Down over the last 0.06 beat, held 0.08, released by 0.22.
        if (d > -0.06 && d < 0.22)
            press = Math.max(press, d < 0 ? 1 + d / 0.06 : d < 0.08 ? 1 : 1 - (d - 0.08) / 0.14);
        if (d >= 0 && d < sinceClick) {
            sinceClick = d;
            clickAt = [p.x, p.y];
        }
    }
    return { x, y, press: clamp(press), sinceClick, clickAt };
}
/** A tick on every click, for `audioEvents`. */
export const clickSounds = (points: Waypoint[], gain = 0.7): SceneSoundEvent[] => points.filter((p) => p.click).map((p) => ({ beat: p.beat, sfx: 'tick' as const, gain }));
// --- Typing -------------------------------------------------------------------------------------------
export type Typed = {
    /** The part typed so far. */
    text: string;
    /** 0–1 share of characters typed. */
    progress: number;
    /** Caret visibility: steady while typing, blinking once per beat otherwise. */
    caret: number;
};
/**
 * `text` typed between local beats b0 and b1. Keystrokes vary a little in spacing (from `seed`) and
 * pause after spaces and punctuation; the last character lands on b1.
 */
export function typing(env: Beat, text: string, b0: number, b1: number, seed = 1): Typed {
    const list = chars(text);
    const weights = list.map((_, i) => {
        const prev = list[i - 1] ?? '';
        const pause = prev === ' ' ? 0.7 : /[,.;:!?，。、]/.test(prev) ? 1.4 : 0;
        return 0.7 + 0.6 * hash01(i, seed, 17) + pause;
    });
    const total = weights.reduce((a, w) => a + w, 0);
    const b = env.localBeat;
    let n = 0;
    let acc = 0;
    for (let i = 0; i < list.length; i++) {
        acc += weights[i]!;
        if (b0 + ((b1 - b0) * acc) / total <= b + 1e-9)
            n = i + 1;
        else
            break;
    }
    const typingNow = b >= b0 && b < b1;
    return {
        text: list.slice(0, n).join(''),
        progress: list.length ? n / list.length : 1,
        caret: typingNow ? 1 : fract(b) < 0.55 ? 1 : 0,
    };
}
// --- Morph --------------------------------------------------------------------------------------------
export type Shape = Box & {
    r: number;
    fill: string;
};
/** A box (with radius and fill) part way between two shapes: the base of container morphs. */
export function morph(a: Shape, b: Shape, k: number): Shape {
    return {
        x: lerp(a.x, b.x, k),
        y: lerp(a.y, b.y, k),
        w: lerp(a.w, b.w, k),
        h: lerp(a.h, b.h, k),
        r: lerp(Math.min(a.r, a.w / 2, a.h / 2), Math.min(b.r, b.w / 2, b.h / 2), k),
        fill: mix(a.fill, b.fill, clamp(k)),
    };
}
// --- Rolling digits -----------------------------------------------------------------------------------
const DIGIT = /\d/;
/** Width of `value` as `odometer` sets it, with the current ctx.font. */
export function odometerWidth(ctx: Ctx, value: string): number {
    return chars(value).reduce((w, c) => w + ctx.measureText(c).width, 0);
}
/**
 * Draw `value` with its digits rolling into place: each digit wheel turns from 0 through a few
 * revolutions and stops on its digit, the rightmost last. Everything else (signs, separators, units)
 * stays put. At k = 1 the drawn text is exactly `value`. Uses the current ctx.font and fillStyle;
 * (x, y) is the left baseline. Returns the width.
 */
export function odometer(ctx: Ctx, value: string, k: number, x: number, y: number): number {
    const list = chars(value);
    const col = Math.max(...'0123456789'.split('').map((d) => ctx.measureText(d).width));
    const digits = list.filter((c) => DIGIT.test(c)).length;
    const prevAlign = ctx.textAlign;
    const prevBaseline = ctx.textBaseline;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    const asc = ctx.measureText('0').actualBoundingBoxAscent || col * 1.3;
    // Clip a little above and below the digits; neighbours on the wheel sit one step apart.
    const h = asc * 1.5;
    const step = asc * 1.12;
    let cx = x;
    let d = 0;
    const top = y - asc - (h - asc) / 2;
    // Each character keeps its own slot, so the resting number is set exactly like text ("1" is
    // narrow); a wheel may show a wider digit while it turns, clipped to the widest digit.
    for (const c of list) {
        if (!DIGIT.test(c)) {
            // Separators and units stay put, so "6,500" rolls up from "0,000", not "0 000".
            const w = ctx.measureText(c).width;
            ctx.fillText(c, cx + w / 2, y);
            cx += w;
            continue;
        }
        const target = Number(c);
        // Later wheels turn more and stop later.
        const order = digits <= 1 ? 1 : d / (digits - 1);
        const turns = 1 + Math.round(order * 2);
        const end = 0.7 + 0.3 * order;
        const kk = reach(clamp(k / end));
        const pos = kk * (turns * 10 + target);
        const base = Math.floor(pos + 1e-9);
        const off = pos - base;
        const slot = ctx.measureText(c).width;
        const center = cx + slot / 2;
        ctx.save();
        ctx.beginPath();
        ctx.rect(center - col / 2 - 2, top, col + 4, h);
        ctx.clip();
        if (off < 1e-6)
            ctx.fillText(String(base % 10), center, y);
        else {
            // The leaving digit fades as it rises, the arriving one as it comes up into place.
            const a = ctx.globalAlpha;
            ctx.globalAlpha = a * (1 - off) ** 1.5;
            ctx.fillText(String(base % 10), center, y - off * step);
            ctx.globalAlpha = a * off ** 1.5;
            ctx.fillText(String((base + 1) % 10), center, y + (1 - off) * step);
            ctx.globalAlpha = a;
        }
        ctx.restore();
        cx += slot;
        d++;
    }
    ctx.textAlign = prevAlign;
    ctx.textBaseline = prevBaseline;
    return cx - x;
}
// --- Tilt ---------------------------------------------------------------------------------------------
/**
 * Draw `draw` as if the card in `box` were turned by `rx` (top away) and `ry` (right side away)
 * radians. Canvas 2D has no perspective; this is an affine stand-in that reads as a tilt up to
 * about 8°.
 */
export function tilt(ctx: Ctx, box: Box, rx: number, ry: number, draw: () => void): void {
    const cx = box.x + box.w / 2;
    const cy = box.y + box.h / 2;
    ctx.save();
    ctx.translate(cx, cy);
    ctx.transform(Math.cos(ry), Math.sin(ry) * 0.22, -Math.sin(rx) * 0.22, Math.cos(rx), 0, 0);
    ctx.translate(-cx, -cy);
    draw();
    ctx.restore();
}
